'use client'

import { usePathname } from 'next/navigation'
import { createContext, type ReactNode, useContext, useRef, useState } from 'react'
import { useMount } from 'ahooks'
import { QueryClientProvider, QueryClient } from "@tanstack/react-query"
import { useStore } from 'zustand'

import { cruzSocket } from '@/lib'
import { Cruz } from '@cruz-com/lookup'
import { createGlobalStore, type GlobalStore } from './store'

export type GlobalStoreApi = ReturnType<typeof createGlobalStore>
export const GlobalStoreContext = createContext<GlobalStoreApi | undefined>(undefined)

export const GlobalStoreProvider = ({ children, user }: { children: ReactNode, user: Cruz.User | null }) => {
  const pathName = usePathname()
  useMount(() => {
    if (!cruzSocket.socket) {
      cruzSocket.init(pathName)
    }
  })
  const [queryClient] = useState(() => new QueryClient())
  const storeRef = useRef<GlobalStoreApi>(null)
  if (!storeRef.current) {
    storeRef.current = createGlobalStore({
      user,
      showSignIn: false,
      leadType: 0,
      scrollTopOver: false,
      showFeedback: false,
    })
  }
  return (
    <GlobalStoreContext.Provider value={storeRef.current}>
      <QueryClientProvider client={queryClient}>
        {children}
      </QueryClientProvider>
    </GlobalStoreContext.Provider>
  )
}

export const useGlobalStore = <T,>(selector: (store: GlobalStore) => T): T => {
  const globalStoreContext = useContext(GlobalStoreContext)
  if (!globalStoreContext) {
    throw new Error(`useGlobalStore must be used within GlobalStoreProvider`)
  }
  return useStore(globalStoreContext, selector)
}
